import { useEffect, useState } from "react";
import { Paper, HoverCard, Text, Title, Button, Popover } from "@mantine/core";
import useReportStore from "../../../store/useReportStore";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mantine/hooks";
import { JobPositionData } from "../../../types/api";
import { getJobDetailsByTitle } from "../../../api/reportApi";
import { JobDetailsModal } from "../../../utils/components/JobDetailsModal";
import useProfileStore from "../../../store/useProfileStore";

const COLORS = ["#05D8D2", "#A64DD7", "#6F40E2", "#3A8BD9"];

type MergedRolesArray = {
  title: string;
  description: string;
};

const CloudOfProfessions: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { report } = useReportStore();
  const { t } = useTranslation();
  const matches = useMediaQuery("(max-width: 1100px)");
  const roles: MergedRolesArray[] = [];

  if (report.optimal_positions) {
    report.optimal_positions.data.forEach((role) => {
      roles.push({
        title: role.title,
        description: role.description,
      });
    });
  }

  if (report.higher_positions) {
    report.higher_positions.data.forEach((role) => {
      roles.push({
        title: role.title,
        description: role.description,
      });
    });
  }

  if (report.optimal_positions_switch) {
    report.optimal_positions_switch.data.forEach((role) => {
      roles.push({
        title: role.title,
        description: role.description,
      });
    });
  }

  if (report.unexpected_roles) {
    report.unexpected_roles.data.forEach((role) => {
      roles.push({
        title: role.title,
        description: role.description,
      });
    });
  }

  const careerPaths = isMobile ? roles.slice(0, 6) : roles;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getValidPosition = (() => {
    const gridRows = 3;
    const gridCols = isMobile ? 4 : 5;
    const cellHeight = 90 / gridRows;
    const cellWidth = (isMobile ? 60 : 80) / gridCols;

    const usedCells = new Set<string>();
    const usedPositions: { top: number; left: number }[] = [];

    const quadrantCount = {
      topLeft: 0,
      topRight: 0,
      bottomLeft: 0,
      bottomRight: 0,
    };

    const MAX_PER_QUADRANT = Math.ceil(careerPaths.length / 4);

    // Мобільна логіка
    const mobileTopSlots: number[] = [];
    const mobileBottomSlots: number[] = [];

    const initMobileSlots = () => {
      if (mobileTopSlots.length || mobileBottomSlots.length) return;

      const total = careerPaths.length;
      const rowHeight = 6; // у відсотках, тобто ~30-40px

      // Поділимо порівну або з перевагою вгору
      const topCount = Math.ceil(total / 2);
      const bottomCount = total - topCount;

      const topStart = 0;
      const topEnd = 32;
      const bottomStart = 68;
      const bottomEnd = 100;

      const topStep = (topEnd - topStart - rowHeight) / (topCount - 1 || 1);
      const bottomStep =
        (bottomEnd - bottomStart - rowHeight) / (bottomCount - 1 || 1);

      for (let i = 0; i < topCount; i++) {
        mobileTopSlots.push(topStart + i * topStep);
      }
      for (let i = 0; i < bottomCount; i++) {
        mobileBottomSlots.push(bottomStart + i * bottomStep);
      }
    };

    initMobileSlots();

    return (): { top: number; left: number } => {
      if (isMobile) {
        const useTop = mobileTopSlots.length > 0;
        const slots = useTop ? mobileTopSlots : mobileBottomSlots;

        if (slots.length === 0) {
          // fallback: розміщуємо нижче заголовка з запасом
          return { top: 90 + Math.random() * 5, left: 10 };
        }

        const slotIndex = Math.floor(Math.random() * slots.length);
        const top = slots.splice(slotIndex, 1)[0]; // видаляємо обрану позицію
        const left = 5 + Math.random() * 5; // 5% – 10% для трохи рандому

        return { top, left };
      }

      // --- Десктопна логіка ---
      for (let attempt = 0; attempt < 200; attempt++) {
        const row = Math.floor(Math.random() * gridRows);
        const col = Math.floor(Math.random() * gridCols);
        const cellKey = `${row}-${col}`;
        if (usedCells.has(cellKey)) continue;

        const top =
          row * cellHeight +
          Math.random() * (cellHeight * 0.8) +
          cellHeight * 0.1;
        const left =
          col * cellWidth + Math.random() * (cellWidth * 0.8) + cellWidth * 0.1;

        const isInTitleArea = (t: number, l: number) =>
          t > 32 && t < 68 && l > 23 && l < 60;

        if (isInTitleArea(top, left)) continue;

        const minDistance = 12;
        const isTooClose = usedPositions.some(
          (pos) => Math.hypot(pos.top - top, pos.left - left) < minDistance
        );
        if (isTooClose) continue;

        const quadrant =
          top < 50
            ? left < 50
              ? "topLeft"
              : "topRight"
            : left < 50
            ? "bottomLeft"
            : "bottomRight";

        if (quadrantCount[quadrant] >= MAX_PER_QUADRANT) continue;

        usedCells.add(cellKey);
        usedPositions.push({ top, left });
        quadrantCount[quadrant]++;
        return { top, left };
      }

      return {
        top: Math.random() * 85 + 5,
        left: Math.random() * 80 + 5,
      };
    };
  })();

  const [opened, setOpened] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobPositionData | null>(null);
  const [loading, setLoading] = useState(false);
  const { profileLanguage } = useProfileStore();

  const handleOpen = async (title: string) => {
    setLoading(true);
    try {
      const res = await getJobDetailsByTitle(
        title,
        Object.keys(profileLanguage!)[0]
      );
      if (res) {
        setCurrentJob(res.data);
        setOpened(true);
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title mb={8} size={matches ? 24 : 36}>
        {t("learn_about_professions")}
      </Title>
      <div className={`career-map-container `}>
        <div className="center-title">{t("explore_career")}</div>
        <div className={`career-paths`}>
          {careerPaths.map((career, index) => {
            const initialPos = getValidPosition();
            return (
              <AnimatedCareerPath
                key={index}
                career={career}
                color={COLORS[index % COLORS.length]}
                initialPos={initialPos}
                index={index}
                isMobile={isMobile}
                onOpen={handleOpen}
                loading={loading}
              />
            );
          })}
        </div>
      </div>
      <JobDetailsModal
        opened={opened}
        job={currentJob!}
        onClose={() => {
          setOpened(false);
          setCurrentJob(null);
        }}
        setCurrentJob={setCurrentJob}
      />
    </Paper>
  );
};

const AnimatedCareerPath: React.FC<{
  career: {
    title: string;
    description: string;
  };
  color: string;
  initialPos: { top: number; left: number };
  index: number;
  isMobile: boolean;
  onOpen: (title: string) => void;
  loading: boolean;
}> = ({ career, color, initialPos, index, isMobile, onOpen, loading }) => {
  const [position, setPosition] = useState(initialPos);
  const { t } = useTranslation();
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  useEffect(() => {
    const moveRandomly = () => {
      setPosition(() => {
        const newTop = initialPos.top + (Math.random() * 2 - 1) * 1;
        const newLeft = initialPos.left + (Math.random() * 2 - 1) * 1;

        return {
          top: Math.max(5, Math.min(newTop, 85)),
          left: Math.max(5, Math.min(newLeft, 80)),
        };
      });

      setTimeout(moveRandomly, Math.random() * 3000 + 3000);
    };

    moveRandomly();
  }, []);
  const fontSize = !isMobile ? 16 + ((index % 6) * (22 - 16)) / 5 : 12;
  return isTouchDevice ? (
    <Popover width={280} position="top" withArrow shadow="md">
      <Popover.Target>
        <div
          className="career-path"
          style={{
            top: `${position.top}%`,
            left: `${position.left}%`,
            transition: "top 3s ease-in-out, left 3s ease-in-out",
            cursor: "pointer",
            fontSize: `${fontSize}px`,
          }}
        >
          <span
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              backgroundColor: color,
              flexShrink: 0,
            }}
          ></span>
          {career.title}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="lg" fw="bold">
          {career.title}
        </Text>
        <Text size="m" mt={6}>
          <strong>{t("career.description")}: </strong>
          {career.description}
        </Text>
        <Button
          mt={10}
          loading={loading}
          onClick={() => onOpen(career.title)}
          variant="outline"
        >
          {t("learn_more")}
        </Button>
      </Popover.Dropdown>
    </Popover>
  ) : (
    <HoverCard width={280} shadow="md" position="top">
      <HoverCard.Target>
        <div
          className="career-path"
          style={{
            top: `${position.top}%`,
            left: `${position.left}%`,
            transition: "top 3s ease-in-out, left 3s ease-in-out",
            cursor: "pointer",
            fontSize: `${fontSize}px`,
          }}
        >
          <span
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              backgroundColor: color,
              flexShrink: 0,
            }}
          ></span>
          {career.title}
        </div>
      </HoverCard.Target>
      <HoverCard.Dropdown w={350}>
        <Text size="lg" fw="bold">
          {career.title}
        </Text>
        <Text size="m" mt={6}>
          <strong> {t("career.description")}: </strong>
          {career.description}
        </Text>

        <Button
          mt={10}
          loading={loading}
          onClick={() => onOpen(career.title)}
          variant="outline"
        >
          {t("learn_more")}
        </Button>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default CloudOfProfessions;
